<template>
  <div class="landing">
    <!-- Top bar -->
    <header class="landing-topbar">
      <div class="brand">
        <img :src="logo" alt="Veridian" class="brand-logo" />
        <span class="brand-name">Veridian</span>
      </div>
      <div class="topbar-actions">
        <LanguageSelector />
        <v-btn variant="text" color="white" class="d-none d-sm-inline-flex" @click="goLogin">
          {{ c.nav_login }}
        </v-btn>
        <v-btn color="white" variant="flat" class="text-primary" @click="goLogin">
          <v-icon start>mdi-login</v-icon>{{ c.nav_login }}
        </v-btn>
      </div>
    </header>

    <!-- Hero -->
    <section class="hero">
      <div class="hero-inner">
        <h1 class="hero-title">{{ c.hero_title }}</h1>
        <p class="hero-subtitle">{{ c.hero_subtitle }}</p>
        <div class="hero-cta">
          <v-btn size="large" color="primary" variant="flat" @click="goLogin">
            <v-icon start>mdi-login</v-icon>{{ c.cta_login }}
          </v-btn>
          <v-btn size="large" color="primary" variant="outlined" @click="goRegister">
            <v-icon start>mdi-account-plus</v-icon>{{ c.cta_register }}
          </v-btn>
          <v-btn size="large" variant="tonal" @click="goDemo">
            <v-icon start>mdi-eye-outline</v-icon>{{ c.cta_visitor }}
          </v-btn>
        </div>
        <p class="hero-note">{{ c.cta_visitor_note }}</p>
      </div>
    </section>

    <!-- Services -->
    <section class="features">
      <h2 class="section-title">{{ c.features_title }}</h2>
      <p class="section-subtitle">{{ c.features_subtitle }}</p>
      <v-container>
        <v-row>
          <v-col v-for="(f, i) in c.features" :key="i" cols="12" sm="6" md="4">
            <v-card class="feature-card" variant="flat" height="100%">
              <v-card-text class="text-center pa-6">
                <v-avatar :color="f.color" size="64" class="mb-4">
                  <v-icon size="32" color="white">{{ f.icon }}</v-icon>
                </v-avatar>
                <h3 class="feature-title">{{ f.title }}</h3>
                <p class="feature-desc">{{ f.desc }}</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Bottom CTA -->
    <section class="bottom-cta">
      <h2 class="bottom-cta-title">{{ c.bottom_title }}</h2>
      <div class="hero-cta">
        <v-btn size="large" color="white" variant="flat" class="text-primary" @click="goRegister">
          <v-icon start>mdi-account-plus</v-icon>{{ c.cta_register }}
        </v-btn>
        <v-btn size="large" color="white" variant="outlined" @click="goDemo">
          <v-icon start>mdi-eye-outline</v-icon>{{ c.cta_visitor }}
        </v-btn>
      </div>
    </section>

    <!-- Footer -->
    <footer class="landing-footer">
      <span>© {{ year }} Veridian</span>
      <span class="footer-links">
        <a href="#" @click.prevent="goLogin">{{ c.nav_login }}</a>
        <router-link to="/legal/terms">{{ c.footer_terms }}</router-link>
        <router-link to="/meta_policy">{{ c.footer_privacy }}</router-link>
      </span>
    </footer>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import logo from "@/assets/logo.png";
import LanguageSelector from "@/components/LanguageSelector.vue";
import { currentLocale } from "@/composables/useLanguages";
import { getToken } from "@/composables/useTokenStorage";

const COPY = {
  ro: {
    nav_login: "Autentificare",
    hero_title: "Platforma ta de telecom, într-un singur loc",
    hero_subtitle: "Gestionează SIM-uri, numere de telefon, servicii, comisioane și rapoarte — totul dintr-o interfață simplă, pentru parteneri și clienți deopotrivă.",
    cta_login: "Autentificare",
    cta_register: "Creează cont",
    cta_visitor: "Intră ca vizitator",
    cta_visitor_note: "Vrei doar să arunci o privire? Explorează platforma în mod demonstrativ, fără cont.",
    features_title: "Ce poți face",
    features_subtitle: "Tot ce ai nevoie pentru a-ți administra serviciile mobile.",
    bottom_title: "Gata de start?",
    footer_terms: "Termeni și condiții",
    footer_privacy: "Confidențialitate",
    features: [
      { icon: "mdi-sim", color: "indigo", title: "Cumpără SIM", desc: "Comandă SIM-uri noi online și activează-le rapid, prepaid sau postpaid." },
      { icon: "mdi-phone-sync", color: "teal", title: "Portează-ți numărul", desc: "Adu-ți numărul actual la noi prin portare, fără să-l pierzi." },
      { icon: "mdi-shield-lock", color: "red", title: "Protejează un număr", desc: "Blochează portarea și protejează-ți numărul împotriva modificărilor neautorizate." },
      { icon: "mdi-sim-outline", color: "blue", title: "Management flotă SIM-uri", desc: "Administrează zeci sau mii de SIM-uri dintr-un singur panou, cu grupuri și reguli." },
      { icon: "mdi-chip", color: "deep-purple", title: "Flotă SIM-uri IoT", desc: "SIM-uri dedicate pentru dispozitive IoT / M2M, cu control de trafic și conectivitate." },
      { icon: "mdi-numeric", color: "green", title: "Numere multiple pe același SIM", desc: "Provizionează mai multe numere individuale pe același SIM (multi-MSISDN)." },
    ],
  },
  en: {
    nav_login: "Sign in",
    hero_title: "Your telecom platform, all in one place",
    hero_subtitle: "Manage SIMs, phone numbers, services, commissions and reports — all from one simple interface, for partners and customers alike.",
    cta_login: "Sign in",
    cta_register: "Create account",
    cta_visitor: "Enter as visitor",
    cta_visitor_note: "Just want a look around? Explore the platform in demo mode, no account needed.",
    features_title: "What you can do",
    features_subtitle: "Everything you need to run your mobile services.",
    bottom_title: "Ready to start?",
    footer_terms: "Terms & Conditions",
    footer_privacy: "Privacy",
    features: [
      { icon: "mdi-sim", color: "indigo", title: "Buy a SIM", desc: "Order new SIM cards online and activate them fast, prepaid or postpaid." },
      { icon: "mdi-phone-sync", color: "teal", title: "Port your number", desc: "Bring your current number to us through porting, without losing it." },
      { icon: "mdi-shield-lock", color: "red", title: "Protect a number", desc: "Lock port-out and protect your number against unauthorized changes." },
      { icon: "mdi-sim-outline", color: "blue", title: "SIM fleet management", desc: "Manage tens or thousands of SIMs from a single panel, with groups and rules." },
      { icon: "mdi-chip", color: "deep-purple", title: "IoT SIM fleet", desc: "Dedicated SIMs for IoT / M2M devices, with traffic control and connectivity." },
      { icon: "mdi-numeric", color: "green", title: "Multiple numbers on one SIM", desc: "Provision several individual numbers on the same SIM (multi-MSISDN)." },
    ],
  },
};

export default defineComponent({
  name: 'LandingPage',
  components: { LanguageSelector },
  data() {
    return { logo, year: 2026 };
  },
  computed: {
    c() {
      return COPY[currentLocale.value === 'en' ? 'en' : 'ro'];
    },
  },
  created() {
    // Returning user with a stored token -> let the login view auto-restore the session.
    if (getToken()) {
      this.$router.replace({ path: "/login" });
    }
  },
  methods: {
    goLogin() { this.$router.push({ path: "/login" }); },
    goRegister() { this.$router.push({ path: "/login", query: { register: "1" } }); },
    goDemo() { this.$router.push({ path: "/demo" }); },
  },
});
</script>

<style scoped>
.landing {
  min-height: 100vh;
  background: #f7f8fb;
  display: flex;
  flex-direction: column;
}
.landing-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: linear-gradient(90deg, #1565c0, #1976d2);
  color: #fff;
}
.brand { display: flex; align-items: center; gap: 10px; }
.brand-logo { height: 36px; width: auto; background: #fff; border-radius: 6px; padding: 2px; }
.brand-name { font-size: 1.25rem; font-weight: 700; letter-spacing: .5px; }
.topbar-actions { display: flex; align-items: center; gap: 8px; }
.hero {
  background: linear-gradient(135deg, #1976d2 0%, #5e35b1 100%);
  color: #fff;
  padding: 72px 24px 88px;
  text-align: center;
}
.hero-inner { max-width: 820px; margin: 0 auto; }
.hero-title { font-size: clamp(1.9rem, 4vw, 3rem); font-weight: 800; line-height: 1.15; margin-bottom: 18px; }
.hero-subtitle { font-size: clamp(1rem, 2vw, 1.25rem); opacity: .95; margin-bottom: 32px; }
.hero-cta { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }
.hero-note { margin-top: 18px; font-size: .9rem; opacity: .85; }
.features { padding: 64px 16px 32px; max-width: 1100px; margin: 0 auto; width: 100%; }
.section-title { text-align: center; font-size: 1.9rem; font-weight: 800; color: #1a2233; }
.section-subtitle { text-align: center; color: #5a6275; margin-bottom: 28px; }
.feature-card { border: 1px solid #eceef3; border-radius: 16px; transition: transform .15s ease, box-shadow .15s ease; }
.feature-card:hover { transform: translateY(-4px); box-shadow: 0 10px 30px rgba(20,30,60,.10); }
.feature-title { font-size: 1.15rem; font-weight: 700; margin-bottom: 8px; color: #1a2233; }
.feature-desc { color: #5a6275; font-size: .95rem; line-height: 1.5; }
.bottom-cta { background: linear-gradient(135deg, #5e35b1, #1976d2); color: #fff; text-align: center; padding: 56px 24px; margin-top: 32px; }
.bottom-cta-title { font-size: 1.7rem; font-weight: 800; margin-bottom: 22px; }
.landing-footer {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
  padding: 18px 24px; background: #11151f; color: #aab2c5; font-size: .9rem;
}
.footer-links { display: flex; gap: 18px; }
.footer-links a { color: #aab2c5; text-decoration: none; }
.footer-links a:hover { color: #fff; text-decoration: underline; }
</style>
